import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Clock } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", dob: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await register(form.first_name, form.last_name, form.email, form.password, form.dob);
      if (u?.pending) { setPending(true); return; }
      navigate(u.role === "member" ? "/portal" : "/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBF9F5]">
      <div className="relative hidden lg:flex flex-col items-center justify-center bg-black p-12">
        <img src="/whf-logo.jpg" alt="World Harvest Fellowship - Auckland" className="w-full max-w-md object-contain" />
        <p className="mt-10 max-w-sm text-center font-serif text-xl leading-relaxed text-stone-400">
          “Join our church family and stay connected with your community.”
        </p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <img src="/whf-emblem.png" alt="WHF" className="lg:hidden mb-8 h-14 w-14 object-contain" />
          {pending ? (
            <div data-testid="register-pending">
              <div className="h-12 w-12 rounded-full bg-[#EAF2E8] flex items-center justify-center"><Clock className="h-6 w-6 text-[#2D5A27]" /></div>
              <h2 className="mt-4 font-serif text-3xl font-bold text-[#1C241B]">Awaiting approval</h2>
              <p className="mt-3 text-sm text-[#526050] leading-relaxed">Thanks for registering! Your account needs to be approved by a church administrator before you can sign in. We'll review it shortly — please check back soon.</p>
              <Link to="/" className="mt-6 inline-block font-medium text-[#2D5A27] hover:underline" data-testid="pending-home-link">← Back to home</Link>
            </div>
          ) : (
          <>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#2D5A27]">Create your account</p>
          <h2 className="mt-2 font-serif text-3xl font-bold text-[#1C241B]">Register as a member</h2>
          <p className="mt-2 text-sm text-[#7A8A78]">View your attendance history and giving records any time.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first_name">First name</Label>
                <Input id="first_name" data-testid="register-firstname-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Jane" className="h-11 bg-white" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last name</Label>
                <Input id="last_name" data-testid="register-lastname-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Smith" className="h-11 bg-white" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input id="email" type="email" data-testid="register-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="h-11 bg-white" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dob">Date of birth</Label>
              <Input id="dob" type="date" data-testid="register-dob-input" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="h-11 bg-white" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="register-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="h-11 bg-white" minLength={6} required />
            </div>
            {error && <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="register-error">{error}</p>}
            <Button type="submit" data-testid="register-submit-button" disabled={loading} className="h-11 w-full bg-[#2D5A27] hover:bg-[#23471F] text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-[#7A8A78]">
            Already have an account?{" "}
            <Link to="/login" data-testid="to-login-link" className="font-medium text-[#2D5A27] hover:underline">Sign in</Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
